import { create } from 'zustand';
import { usageApi, DailyUsageInfo } from '../lib/api';

interface DailyUsageState {
  // 일일 사용량
  usage: DailyUsageInfo | null;
  // 로딩 상태
  isLoading: boolean;
  // 에러 메시지
  error: string | null;
  // 마지막 조회 시간
  lastFetchedAt: Date | null;
  // 사용량 초과 모달 표시 여부
  showExhaustedModal: boolean;
  // 초과된 유형 (photo 또는 inspection)
  exhaustedType: 'photo' | 'inspection' | null;

  // 액션
  fetchUsage: () => Promise<void>;
  checkUsage: (type: 'photo' | 'inspection') => Promise<boolean>;
  setShowExhaustedModal: (show: boolean, type?: 'photo' | 'inspection') => void;
  // WebSocket 이벤트로 실시간 업데이트
  updateFromSocket: (type: 'photo' | 'inspection', current: number, limit: number) => void;
}

export const useUsageStore = create<DailyUsageState>()((set, get) => ({
  usage: null,
  isLoading: false,
  error: null,
  lastFetchedAt: null,
  showExhaustedModal: false,
  exhaustedType: null,

  // 일일 사용량 조회
  fetchUsage: async () => {
    // 최근 10초 이내에 조회했으면 스킵 (캐싱)
    const lastFetched = get().lastFetchedAt;
    if (lastFetched && Date.now() - lastFetched.getTime() < 10000) {
      return;
    }

    set({ isLoading: true, error: null });

    try {
      const response = await usageApi.getDaily();
      if (response.success && response.data) {
        set({
          usage: response.data,
          isLoading: false,
          lastFetchedAt: new Date(),
        });
      } else {
        set({
          error: response.error?.message ?? '사용량 조회 실패',
          isLoading: false,
        });
      }
    } catch {
      set({
        error: '사용량 조회 중 오류가 발생했습니다.',
        isLoading: false,
      });
    }
  },

  // 사용 가능 여부 체크
  checkUsage: async (type: 'photo' | 'inspection') => {
    try {
      const response = await usageApi.check(type);
      if (response.success && response.data) {
        // 최신 사용량으로 업데이트
        await get().fetchUsage();
        // 사용량 초과 시 모달 표시
        if (!response.data.canProceed) {
          set({ showExhaustedModal: true, exhaustedType: type });
        }
        return response.data.canProceed;
      }
      return false;
    } catch {
      return false;
    }
  },

  // 사용량 초과 모달 표시/숨김
  setShowExhaustedModal: (show: boolean, type?: 'photo' | 'inspection') => {
    set({
      showExhaustedModal: show,
      exhaustedType: show ? (type ?? null) : null,
    });
  },

  // WebSocket 이벤트로 실시간 업데이트
  updateFromSocket: (type: 'photo' | 'inspection', current: number, limit: number) => {
    const currentUsage = get().usage;
    if (!currentUsage) return;

    // 해당 타입의 카운트만 업데이트
    const updatedUsage = { ...currentUsage };
    if (type === 'photo') {
      updatedUsage.photoCount = current;
      updatedUsage.photoLimit = limit;
    } else {
      updatedUsage.inspectionCount = current;
      updatedUsage.inspectionLimit = limit;
    }

    set({
      usage: updatedUsage,
      lastFetchedAt: new Date(), // 캐시 타임스탬프 갱신
    });
  },
}));

// 유틸리티 함수
export const getUsagePercentage = (used: number, limit: number): number => {
  if (limit === 0) return 100;
  return Math.min((used / limit) * 100, 100);
};

/**
 * 사용량 진행바 fill 색.
 *
 * 소비처는 둘 다 진행바 fill 전용이다(UsageIndicator 사이드바, 설정>구독 관리).
 * 반환값은 완성된 정적 클래스 리터럴이어야 한다 — 소비처가 템플릿 문자열에
 * 끼워 넣기만 하므로, 여기서 조각을 조합하면 Tailwind가 CSS를 생성하지 못한다.
 */
export const getUsageColor = (percentage: number): string => {
  if (percentage >= 100) return 'bg-red-500'; // 소진 — danger 의미색
  if (percentage >= 80) return 'bg-amber-500'; // 임박 — 목업 세만틱 램프는 yellow가 아닌 amber
  return 'bg-brand-500'; // 정상 — 구 브랜드 blue에서 이관
};

/**
 * 다음 초기화 시간까지 남은 시간 계산
 */
export const getTimeUntilReset = (nextResetAt: string): string => {
  const reset = new Date(nextResetAt);
  const now = new Date();
  const diffMs = reset.getTime() - now.getTime();

  if (diffMs <= 0) return '곧 초기화';

  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 0) {
    return `${hours}시간 ${minutes}분 후`;
  }
  return `${minutes}분 후`;
};

// 레거시 호환성을 위한 타입 및 상수
export interface PlanInfo {
  id: string;
  name: string;
  category: 'B2C' | 'B2B';
  price: number;
  photoLimit: number;
  inspectionLimit: number;
  activeUserLimit: number;
}

export interface UsageInfo {
  photosUsed: number;
  photosLimit: number;
  inspectionsUsed: number;
  inspectionsLimit: number;
  activeUsers: number;
  activeUserLimit: number;
  periodStart: string;
  periodEnd: string;
}

// 요금제 목록 (참조용, Phase 2에서 활용)
export const PLANS: PlanInfo[] = [
  // B2C
  { id: 'free', name: '무료', category: 'B2C', price: 0, photoLimit: 50, inspectionLimit: 2, activeUserLimit: 1 },
  { id: 'personal', name: '개인', category: 'B2C', price: 30000, photoLimit: 600, inspectionLimit: 30, activeUserLimit: 1 },
  { id: 'pro', name: '프로', category: 'B2C', price: 80000, photoLimit: 1800, inspectionLimit: 90, activeUserLimit: 3 },
  // B2B
  { id: 'starter', name: 'Starter', category: 'B2B', price: 100000, photoLimit: 1200, inspectionLimit: 60, activeUserLimit: 2 },
  { id: 'basic', name: 'Basic', category: 'B2B', price: 250000, photoLimit: 3000, inspectionLimit: 150, activeUserLimit: 5 },
  { id: 'standard', name: 'Standard', category: 'B2B', price: 600000, photoLimit: 9000, inspectionLimit: 450, activeUserLimit: 15 },
  { id: 'enterprise', name: 'Enterprise', category: 'B2B', price: 1500000, photoLimit: 24000, inspectionLimit: 1200, activeUserLimit: 40 },
];

export const formatPrice = (price: number): string => {
  if (price === 0) return '무료';
  return `₩${price.toLocaleString('ko-KR', {})}`;
};

export const getDaysRemaining = (periodEnd: string): number => {
  const end = new Date(periodEnd);
  const now = new Date();
  const diffTime = end.getTime() - now.getTime();
  return Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
};
